import {
  GetProjectsResponse,
  AddProjectPayload,
  AddProjectResponse,
  EditProjectPayload,
  EditProjectResponse,
  DeleteProjectResponse,
  UpdateProjectSettingsPayload,
  UpdateProjectSettingsResponse,
  RemoveUserResponse,
  AddZoneToCorrelationPayload,
  AddZoneToCorrelationResponse,
  DeleteZoneResponse,
  CalculateCorrelationPayload,
  CalculateCorrelationResponse,
  UpdateSelectedFziFunctionsPayload,
  UpdateSelectedFziFunctionsResponse,
  UpdateZonesPayload,
  UpdateZoneBoundariesPayload,
  UpdateHfuBandsPayload,
  ExportDataPayload,
  DuplicateProjectPayload,
  DuplicateProjectResponse,
  ExportProjectPackageResponse,
  ImportProjectPayload,
  ImportProjectResponse,
  AddUserToProjectResponse,
  UpdateUserRoleFromProjectResponse,
  ConvertUnitPayload,
  RunAutoCorrelationPayload,
  RunAutoCorrelationResponse,
  ApplyAutoCorrelationPayload,
  ApplyAutoCorrelationResponse,
  GetLatestAutoCorrelationResponse,
} from '../@types/projects/types';
import {
  AddWellPayload,
  AddWellResponse,
  DeleteWellResponse,
  ListWellsResponse,
  UpdateWellPayload,
  UpdateWellResponse
} from '../@types/wells/types';
import {
  AddSamplePayload,
  AddSampleResponse,
  CalculateProjectPayload,
  CalculateSamplePayload,
  CalculateSampleResponse,
  DeleteSampleResponse,
  ListAllProjectSamplesResponse,
  ListSamplesResponse,
  Sample,
  UpdateSamplePayload,
  UpdateSampleResponse
} from '../@types/samples/types';
import {
  GheInputsResponse,
  GheInputsUpsertResponse,
  GheInputSessionUpsert
} from '../@types/gheInputs/types';
import {
  CorrelationEndpointPreviewRequest,
  CorrelationEndpointPreviewResponse,
  CorrelationPreferencesPayload,
  CorrelationPreferencesResponse
} from '../@types/data/types';
import { GheResultsResponse } from '../@types/gheResults/types';
import { GheAnalogueTablesResponse } from '../@types/gheAnalogueTables/types';
import {
  GhePage4ExportRequest,
  GhePage4Response,
  GhePage4SummaryCurves,
  ProcessAnalogueSelection
} from '../@types/ghePage4/types';
import {
  IxTableCsvExportRequest,
  IxTableResponse
} from '../@types/ghePage5/types';
import {
  CoreUtilityAnalogueSearchRequest,
  CoreUtilityAnalogueSearchResponse,
  CoreUtilityBestFitRequest,
  CoreUtilityBestFitResponse,
  CoreUtilityComputeRequest,
  CoreUtilityComputeResponse
} from '../@types/gheCoreUtilities/types';
import {
  MonotonicPredictionRequest,
  MonotonicPredictionResult,
  MonotonicPredictionResponse,
  MonotonicTrainingRequest,
  MonotonicTrainingResponse
} from '../@types/gheMonotonicMl/types';
import {
  PackagedAssetAvailabilityResponse,
  PackagedAssetBundleResponse,
  PackagedAssetIngestionRequest,
  PackagedAssetIngestionResponse
} from '../@types/ghePackagedAssets/types';
import {
  PcaDiagnosticsResponse,
  UnseenInferenceRequest,
  UnseenInferenceResponse
} from '../@types/gheUnseenInference/types';
import {
  BatchImportParseResponse,
  BatchImportParseResponseContent,
  SaveImportedScalSamplesResponse
} from '../@types/import/types';
import {
  ActivateIftArtifactBundlePayload,
  ActivateIftArtifactBundleResponse,
  IftUsageProvenanceResponse,
  RegisterIftTrainingRunPayload,
  RegisterIftTrainingRunResponse
} from '../@types/gheIftArtifacts/types';
import {
  SwHeightApiResponse,
  SwHeightComputePayload,
  SwHeightComputeResponseContent,
  SwHeightData,
  SwHeightDatasetKind,
  SwHeightDefaults,
  SwHeightSeedResult,
  SwHeightSettings,
  WellLogImportPreview
} from '../@types/swHeight/types';
import { API } from './api';
import { UserRole } from '../@types/users/user';

const normalizeGheResultsPayload = (content: any) => {
  if (!content) {
    return content;
  }

  const normalizedResults = Array.isArray(content.results)
    ? content.results
    : Array.isArray(content.target_rocks)
    ? content.target_rocks
    : [];

  return {
    ...content,
    results: normalizedResults
  };
};

const normalizeGheAnalogueTablesPayload = (content: any) => {
  if (!content) {
    return content;
  }

  const normalizedTables = Array.isArray(content.tables)
    ? content.tables
    : Array.isArray(content.results)
    ? content.results
    : [];

  return {
    ...content,
    tables: normalizedTables
  };
};

const normalizeGhePage4Payload = (content: any) => {
  if (!content) {
    return content;
  }

  const normalizedRocks = Array.isArray(content.rocks)
    ? content.rocks
    : Array.isArray(content.target_rocks)
    ? content.target_rocks
    : [];

  return {
    ...content,
    rocks: normalizedRocks
  };
};

const normalizeIxTablePayload = (content: any) => {
  if (!content) {
    return content;
  }

  const rowCount =
    typeof content.row_count === 'number'
      ? content.row_count
      : typeof content.total_rows === 'number'
      ? content.total_rows
      : Array.isArray(content.rows)
      ? content.rows.length
      : 0;

  return {
    ...content,
    row_count: rowCount
  };
};

const toFiniteNumberOrNull = (value: unknown): number | null => {
  const casted = Number(value);
  return Number.isFinite(casted) ? casted : null;
};

const apiErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string; detail?: string } } }).response;
    return response?.data?.message || response?.data?.detail || fallback;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const normalizePcaDiagnosticsPayload = (content: any) => {
  if (!content) {
    return content;
  }

  const normalizedPoints = Array.isArray(content.points)
    ? content.points.map((point: any) => ({
        ...point,
        pc1: toFiniteNumberOrNull(point?.pc1) ?? 0,
        pc2: toFiniteNumberOrNull(point?.pc2) ?? 0,
        distance_to_reference: toFiniteNumberOrNull(point?.distance_to_reference)
      }))
    : [];

  return {
    ...content,
    points: normalizedPoints,
    plot_series: Array.isArray(content.plot_series) ? content.plot_series : []
  };
};

const normalizeIftUsageProvenancePayload = (content: any) => {
  if (!content || typeof content !== 'object') {
    return null;
  }

  const tabProvenance = Array.isArray(content.tab_provenance)
    ? content.tab_provenance
    : [];

  return {
    project_uid: typeof content.project_uid === 'string' ? content.project_uid : '',
    active_artifact_revision:
      typeof content.active_artifact_revision === 'string'
        ? content.active_artifact_revision
        : null,
    compatibility_state:
      content.compatibility_state === 'compatible' ||
      content.compatibility_state === 'incompatible' ||
      content.compatibility_state === 'unknown'
        ? content.compatibility_state
        : 'unknown',
    tab_provenance: tabProvenance.map((item: any) => ({
      ...item,
      prediction_path:
        item?.prediction_path === 'official_artifact' ||
        item?.prediction_path === 'fallback_knn' ||
        item?.prediction_path === 'fallback_dummy'
          ? item.prediction_path
          : 'fallback_dummy',
      reason_code: typeof item?.reason_code === 'string' ? item.reason_code : 'unknown',
      artifact_revision:
        typeof item?.artifact_revision === 'string' ? item.artifact_revision : null
    }))
  };
};

export const getProjects = async () => {
  const resource = `/projects`;
  const response = await API.get<GetProjectsResponse>(resource);

  const content = response.data.content;
  return Array.isArray(content) ? content : [];
}; // OK

export const listWells = async (projectId: string) => {
  const resource = `/wells`;
  const params = { project_uid: projectId };

  const response = await API.get<ListWellsResponse>(resource, {
    params: params
  });

  return response.data.content;
}; // OK

export const addProject = async (project: AddProjectPayload) => {
  const resource = `/projects`;
  const response = await API.post<AddProjectResponse>(resource, project);
  return response.data.content;
}; // OK

export const addUserToProject = async (
  email: string,
  project_uid: string,
  role: string
) => {
  const resource = `user/${project_uid}`;
  const payload = {
    emails: [email],
    role: role,
    project_uid: project_uid
  };

  const response = await API.post<AddUserToProjectResponse>(resource, payload);
  return response.data.content;
}; // OK

export const editProject = async (projectData: EditProjectPayload) => {
  const resource = `/projects`;
  const response = await API.put<EditProjectResponse>(resource, projectData);

  return response.data.content;
}; // OK

export const deleteProject = async (projectId: string) => {
  const resource = `/projects/${projectId}`;
  const response = await API.delete<DeleteProjectResponse>(resource);
  return response;
}; // OK

export const duplicateProject = async (
  projectId: string,
  payload: DuplicateProjectPayload = {}
) => {
  const resource = `/projects/${projectId}/duplicate`;
  const response = await API.post<DuplicateProjectResponse>(resource, payload);
  return response.data.content;
};

export const exportProjectPackage = async (
  projectId: string
): Promise<ExportProjectPackageResponse> => {
  const resource = `/projects/${projectId}/export`;
  const response = await API.post(resource, {}, { responseType: 'blob' });
  const filenameHeader = response.headers['content-disposition'] as string | undefined;
  const filenameMatch = filenameHeader?.match(/filename="?([^";]+)"?/);
  const text = await response.data.text();

  return {
    data: JSON.parse(text),
    filename: filenameMatch?.[1] ?? null
  };
};

export const importProjectPackage = async (payload: ImportProjectPayload) => {
  const resource = `/projects/import`;
  const formData = new FormData();
  formData.append('file', payload.file);
  if (payload.project_name) {
    formData.append('project_name', payload.project_name);
  }
  const response = await API.post<ImportProjectResponse>(resource, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data.content;
};

export const addWell = async (well: AddWellPayload) => {
  const resource = `/wells`;
  const response = await API.post<AddWellResponse>(resource, well);
  return response.data.content;
}; // OK

export const updateWell = async (well: UpdateWellPayload) => {
  const resource = `/wells`;
  const response = await API.put<UpdateWellResponse>(resource, well);
  return response.data.content;
}; // OK

export const listWellSamples = async (wellId: string) => {
  const resource = `/samples`;
  const params = { well_uid: wellId };

  const response = await API.get<ListSamplesResponse>(resource, {
    params: params
  });

  return response.data.content;
};

export const listAllProjectSamples = async (projectId: string) => {
  const resource = `projects/${projectId}/samples`;

  const response = await API.get<ListAllProjectSamplesResponse>(resource);

  return response.data.content;
};

export const calculateSample = async (payload: CalculateSamplePayload) => {
  const resource = "/calculations/calculate_sample";
  const response = await API.post<CalculateSampleResponse>(resource, payload);
  if (response.data.content === null || response.data.content === undefined) {
    throw new Error(response.data.message || 'Calculation failed with no result.');
  }
  return response.data.content;
}

export interface BatchSampleOutcome {
  sample_uid: string;
  ok: boolean;
  sample?: Sample;
  error?: string;
}

export interface CalculateSamplesBatchOptions {
  onResult?: (outcome: BatchSampleOutcome) => void;
  signal?: AbortSignal;
  concurrency?: number;
}

/**
 * Run `calculate_sample` for many samples with a bounded concurrency pool.
 *
 * Phase-1 implementation loops the existing single-sample endpoint so each
 * result streams back through `onResult` as soon as it finishes. A future
 * backend batch endpoint (e.g. POST /calculations/calculate_samples_batch)
 * can replace the body without changing this signature.
 *
 * Cancellation via `signal` stops dispatching new requests; in-flight
 * requests finish and are still reported. A failed sample never aborts the
 * rest of the batch.
 */
export const calculateSamplesBatch = async (
  payloads: CalculateSamplePayload[],
  options: CalculateSamplesBatchOptions = {}
): Promise<BatchSampleOutcome[]> => {
  const { onResult, signal, concurrency = 3 } = options;
  const results: BatchSampleOutcome[] = [];
  let nextIndex = 0;

  const runNext = async (): Promise<void> => {
    while (!signal?.aborted) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= payloads.length) return;

      const payload = payloads[index];
      let outcome: BatchSampleOutcome;
      try {
        const sample = await calculateSample(payload);
        outcome = { sample_uid: payload.sample_uid, ok: true, sample };
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Calculation failed';
        outcome = { sample_uid: payload.sample_uid, ok: false, error: message };
      }
      results.push(outcome);
      onResult?.(outcome);
    }
  };

  const poolSize = Math.max(1, Math.min(concurrency, payloads.length));
  await Promise.all(Array.from({ length: poolSize }, () => runNext()));

  return results;
};

export const calculateProject = async (payload: CalculateProjectPayload) => {
  const resource = "/calculations/calculate_project";
  const response = await API.post<CalculateSampleResponse>(resource, payload);
  if (response.data.content === null || response.data.content === undefined) {
    throw new Error(response.data.message || 'Calculation failed with no result.');
  }
  return response.data.content;
}

export const updateProjectSettings = async (
  payload: UpdateProjectSettingsPayload
) => {
  const resource = `/projects`;
  const response = await API.put<UpdateProjectSettingsResponse>(
    resource,
    payload
  );

  return response.data.content;
};

export const deleteWell = async (wellId: string) => {
  const resource = `/wells/${wellId}`;
  const response = await API.delete<DeleteWellResponse>(resource);

  return response;
};

export const deleteSample = async (sampleId: string) => {
  const resource = `/samples/${sampleId}`;
  const response = await API.delete<DeleteSampleResponse>(resource);

  return response;
};

export const addZoneToCorrelation = async (
  projectId: string,
  payload: AddZoneToCorrelationPayload
) => {
  const resource = `/projects/${projectId}/correlation/zones`;
  const response = await API.post<AddZoneToCorrelationResponse>(
    resource,
    payload
  );

  return response.data.content;
};

export const deleteZone = async (projectId: string, zoneUids: string[]) => {
  const queryString = zoneUids.map(uid => `zone_uids=${uid}`).join('&');
  const resource = `projects/${projectId}/correlation/zones?${queryString}`;
  const response = await API.delete<DeleteZoneResponse>(resource);
  
  return response.data.content;
};

export const addSample = async (payload: AddSamplePayload) => {
  const resource = `/samples`;
  const response = await API.post<AddSampleResponse>(resource, payload);

  return response.data.content;
};

export const updateSample = async (payload: UpdateSamplePayload) => {
  const resource = `/samples`;
  const response = await API.put<UpdateSampleResponse>(resource, payload);

  return response.data.content;
};

export const importScalExcelBatch = async (files: File[]) => {
  const resource = `/data-import/excel/batch`;
  const formData = new FormData();
  files.forEach(file => formData.append('excel_files', file));

  try {
    const response = await API.post<BatchImportParseResponse>(resource, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    if (!response.data.content) {
      throw new Error(response.data.message || 'Excel import parse returned no content.');
    }
    return response.data.content;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to parse SCAL Excel files.'));
  }
};

export type ScalExcelTemplateFormat = 'table' | 'per_sample_tabs';

export const downloadScalExcelTemplate = async (format: ScalExcelTemplateFormat = 'table') => {
  const resource = `/data-import/excel/template`;
  try {
    const response = await API.get<Blob>(resource, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to download SCAL Excel template.'));
  }
};

export const saveImportedScalSamples = async (
  projectUid: string,
  wellUid: string,
  parsedPayload: BatchImportParseResponseContent
) => {
  const resource = `/data-import/save`;
  try {
    const response = await API.post<SaveImportedScalSamplesResponse>(resource, {
      project_uid: projectUid,
      well_uid: wellUid,
      parsed_data: parsedPayload,
      duplicate_strategy: 'reject'
    });
    if (!response.data.content) {
      throw new Error(response.data.message || 'Import save returned no samples.');
    }
    return response.data.content;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to save imported SCAL samples.'));
  }
};

export const removeUserFromProject = async (
  project_uid: string,
  user_email: string
) => {
  const resource = `/user/${project_uid}/${user_email}`;

  const response = await API.delete<RemoveUserResponse>(resource);

  return response.data.content;
};

export const updateUserRoleFromProject = async (
  projectUid: string,
  userEmail: string,
  role: UserRole
) => {
  const resource = `/user/${projectUid}/${userEmail}`;

  const response = await API.patch<UpdateUserRoleFromProjectResponse>(
    resource,
    {
      target_role: role
    }
  );

  return response.data.content;
};

export const calculateCorrelation = async (
  payload: CalculateCorrelationPayload
) => {
  const resource = `/correlation`;

  const response = await API.post<CalculateCorrelationResponse>(
    resource,
    payload
  );

  return response.data.content;
};

export const saveSelectedFziFunctions = async (
  projectId: string,
  payload: UpdateSelectedFziFunctionsPayload
) => {
  const resource = `/projects/${projectId}/correlation/selected_fzi_functions`;
  const response = await API.patch<UpdateSelectedFziFunctionsResponse>(
    resource,
    payload
  );

  return response.data.content;
};

export const runAutoCorrelation = async (
  projectId: string,
  payload: RunAutoCorrelationPayload
) => {
  const resource = `/projects/${projectId}/correlation/auto:run`;
  const response = await API.post<RunAutoCorrelationResponse>(
    resource,
    payload
  );

  return response.data.content;
};

export const applyAutoCorrelation = async (
  projectId: string,
  payload: ApplyAutoCorrelationPayload
) => {
  const resource = `/projects/${projectId}/correlation/auto:apply`;
  const response = await API.post<ApplyAutoCorrelationResponse>(
    resource,
    payload
  );

  return response.data.content;
};

export const getLatestAutoCorrelation = async (projectId: string) => {
  const resource = `/projects/${projectId}/correlation/auto:latest`;
  const response = await API.get<GetLatestAutoCorrelationResponse>(resource);

  return response.data.content;
};

export const updateCorrelationPreferences = async (
  projectId: string,
  payload: CorrelationPreferencesPayload
) => {
  const resource = `/projects/${projectId}/correlation/preferences`;
  const response = await API.patch<CorrelationPreferencesResponse>(
    resource,
    payload
  );

  return response.data.content;
};

export const previewEndpointCorrelationCharts = async (
  projectId: string,
  payload: CorrelationEndpointPreviewRequest
) => {
  const resource = `/projects/${projectId}/correlation/charts:endpoint-preview`;
  const response = await API.post<CorrelationEndpointPreviewResponse>(
    resource,
    payload
  );

  return response.data.content;
};

export const updateZones = async (
  projectId: string,
  payload: UpdateZonesPayload
) => {
  const resource = `/projects/${projectId}/correlation/zones`;
  const response = await API.patch(resource, payload);

  return response.data.content;
};

export const updateZoneBoundaries = async (
  projectId: string,
  payload: UpdateZoneBoundariesPayload
) => {
  const resource = `/projects/${projectId}/correlation/zones`;
  const response = await API.patch(resource, payload);

  return response.data.content;
};

export const updateHfuBands = async (
  projectId: string,
  payload: UpdateHfuBandsPayload
) => {
  const resource = `/projects/${projectId}/correlation/hfu-bands`;
  const response = await API.put(resource, payload);

  return response.data.content;
};

export const exportData = async (payload: ExportDataPayload) => {
  const resource = `/data-export/petrel`;
  const response = await API.post(resource, payload);

  return response.data;
};

export const convertUnit = async (
  payload: ConvertUnitPayload,
  projectId: string
) => {
  const resource = `/projects/${projectId}/correlation/unit_conversion`;
  const response = await API.post(resource, payload);

  return response.data.content;
};

export const getGheInputs = async (projectUid: string) => {
  const resource = `/projects/${projectUid}/ghe-inputs`;
  const response = await API.get<GheInputsResponse>(resource);

  return response.data.content;
};

export const upsertGheInputs = async (
  projectUid: string,
  payload: GheInputSessionUpsert
) => {
  const resource = `/projects/${projectUid}/ghe-inputs`;
  const response = await API.put<GheInputsUpsertResponse>(resource, payload);

  return response.data.content;
};

export const getGheResults = async (projectUid: string) => {
  const resource = `/projects/${projectUid}/ghe-results`;
  const response = await API.get<GheResultsResponse>(resource);

  return normalizeGheResultsPayload(response.data.content);
};

export const getGheAnalogueTables = async (projectUid: string) => {
  const resource = `/projects/${projectUid}/ghe-analogue-tables`;
  const response = await API.get<GheAnalogueTablesResponse>(resource);

  return normalizeGheAnalogueTablesPayload(response.data.content);
};

export const getGhePage4 = async (projectUid: string) => {
  const resource = `/projects/${projectUid}/ghe-page4`;
  const response = await API.get<GhePage4Response>(resource);

  return normalizeGhePage4Payload(response.data.content);
};

export const exportGhePage4Workbook = async (
  projectUid: string,
  payload: GhePage4ExportRequest
) => {
  const resource = `/projects/${projectUid}/ghe-page4/export`;
  const response = await API.post(resource, payload, {
    responseType: 'blob'
  });

  return response.data;
};

export const recalculateGhePage4Summary = async (
  projectUid: string,
  selections: ProcessAnalogueSelection[]
): Promise<Record<string, GhePage4SummaryCurves>> => {
  const resource = `/projects/${projectUid}/ghe-page4/recalculate-summary`;
  const response = await API.post<{ content: Record<string, GhePage4SummaryCurves> }>(
    resource,
    { selections }
  );
  return response.data.content ?? {};
};

export const generateIxTable = async (
  projectUid: string,
  payload: IxTableCsvExportRequest
) => {
  const resource = `/projects/${projectUid}/ghe-page5/ix-tables/generate`;
  const response = await API.post<IxTableResponse>(resource, payload);

  return normalizeIxTablePayload(response.data.content);
};

export const exportIxTableCsv = async (
  projectUid: string,
  payload: IxTableCsvExportRequest
) => {
  const resource = `/projects/${projectUid}/ghe-page5/ix-tables/export-csv`;
  const response = await API.post(resource, payload, {
    responseType: 'blob'
  });

  return response.data;
};

export const computeGheCoreUtilities = async (
  projectUid: string,
  payload: CoreUtilityComputeRequest
) => {
  const resource = `/projects/${projectUid}/ghe-core-utilities/compute`;
  const response = await API.post<CoreUtilityComputeResponse>(resource, payload);

  return response.data.content;
};

export const searchGheCoreUtilityAnalogues = async (
  projectUid: string,
  payload: CoreUtilityAnalogueSearchRequest
) => {
  const resource = `/projects/${projectUid}/ghe-core-utilities/analogue-search`;
  const response = await API.post<CoreUtilityAnalogueSearchResponse>(resource, payload);

  return response.data.content;
};

export const deriveGheCoreUtilityBestFit = async (
  projectUid: string,
  payload: CoreUtilityBestFitRequest
) => {
  const resource = `/projects/${projectUid}/ghe-core-utilities/best-fit`;
  const response = await API.post<CoreUtilityBestFitResponse>(resource, payload);

  return response.data.content;
};

export const trainGheMonotonicModels = async (
  projectUid: string,
  payload: MonotonicTrainingRequest
) => {
  const resource = `/projects/${projectUid}/ghe-ml/train`;
  const response = await API.post<MonotonicTrainingResponse>(resource, payload);

  return response.data.content;
};

export const predictGheMonotonicParameters = async (
  projectUid: string,
  payload: MonotonicPredictionRequest
) => {
  const resource = `/projects/${projectUid}/ghe-ml/predict`;
  const response = await API.post<MonotonicPredictionResponse>(resource, payload);

  const content = response.data.content;
  if (!content || !content.metadata) {
    return content;
  }
  // Normalize metadata path in case backend adds new enum values in future.
  const allowedPaths = new Set(['primary_lgbm', 'fallback_knn', 'fallback_dummy']);
  const normalizedPath = allowedPaths.has(content.metadata.path)
    ? content.metadata.path
    : 'fallback_dummy';

  return {
    ...content,
    metadata: {
      ...content.metadata,
      path: normalizedPath
    }
  } as MonotonicPredictionResult;
};

export const ingestGhePackagedAssets = async (
  projectUid: string,
  payload: PackagedAssetIngestionRequest
) => {
  const resource = `/projects/${projectUid}/ghe-assets/ingestions/packaged`;
  const response = await API.post<PackagedAssetIngestionResponse>(resource, payload);

  return response.data.content;
};

export const getGhePackagedAssetBundle = async (
  projectUid: string,
  bundleUid: string
) => {
  const resource = `/projects/${projectUid}/ghe-assets/bundles/${bundleUid}`;
  const response = await API.get<PackagedAssetBundleResponse>(resource);

  return response.data.content;
};

export const getGhePackagedAssetAvailability = async (projectUid: string) => {
  const resource = `/projects/${projectUid}/ghe-assets/availability`;
  const response = await API.get<PackagedAssetAvailabilityResponse>(resource);

  return response.data.content;
};

export const registerGheIftModelTraining = async (
  projectUid: string,
  payload: RegisterIftTrainingRunPayload
) => {
  const resource = `/projects/${projectUid}/ghe-assets/ift-model/trainings`;
  const response = await API.post<RegisterIftTrainingRunResponse>(resource, payload);
  return response.data.content;
};

export const activateGheIftModelArtifact = async (
  projectUid: string,
  payload: ActivateIftArtifactBundlePayload
) => {
  const resource = `/projects/${projectUid}/ghe-assets/ift-model/activate`;
  const response = await API.post<ActivateIftArtifactBundleResponse>(resource, payload);
  return response.data.content;
};

export const getGheIftArtifactProvenance = async (projectUid: string) => {
  const resource = `/projects/${projectUid}/ghe-assets/ift-model/provenance`;
  const response = await API.get<IftUsageProvenanceResponse>(resource);
  return normalizeIftUsageProvenancePayload(response.data.content);
};

export const runGheUnseenInference = async (
  projectUid: string,
  payload: UnseenInferenceRequest
) => {
  const resource = `/projects/${projectUid}/ghe-unseen/infer`;
  const response = await API.post<UnseenInferenceResponse>(resource, payload);
  const content = response.data.content;
  if (!content) {
    return content;
  }

  return {
    ...content,
    pca_diagnostics: normalizePcaDiagnosticsPayload(content.pca_diagnostics)
  };
};

export const getGheUnseenPcaDiagnostics = async (projectUid: string) => {
  const resource = `/projects/${projectUid}/ghe-unseen/diagnostics/pca`;
  const response = await API.get<PcaDiagnosticsResponse>(resource);
  return normalizePcaDiagnosticsPayload(response.data.content);
};

// ---------------------------------------------------------------------------
// Sw-Height Calibration
// ---------------------------------------------------------------------------

export const importSwHeightLogs = async (
  projectUid: string,
  file: File,
  dataset: SwHeightDatasetKind = 'log'
) => {
  const resource = `/sw-height/projects/${projectUid}/logs/import?dataset=${dataset}`;
  const formData = new FormData();
  formData.append('excel_file', file);
  try {
    const response = await API.post<SwHeightApiResponse<WellLogImportPreview>>(
      resource,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    if (!response.data.content) {
      throw new Error(response.data.message || 'Well log import returned no content.');
    }
    return response.data.content;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to parse well log file.'));
  }
};

export const saveSwHeightLogs = async (
  projectUid: string,
  preview: WellLogImportPreview,
  dataset: SwHeightDatasetKind = 'log'
) => {
  const resource = `/sw-height/projects/${projectUid}/logs?dataset=${dataset}`;
  try {
    const response = await API.post<SwHeightApiResponse<{ row_count: number }>>(
      resource,
      {
        curves: preview.curves,
        source_filename: preview.filename,
        warnings: preview.warnings
      }
    );
    if (!response.data.content) {
      throw new Error(response.data.message || 'Well log save returned no content.');
    }
    return response.data.content;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to save well logs.'));
  }
};

/**
 * Builds the core/RCAL dataset from the project's SCAL samples. Depth,
 * porosity and permeability come from the samples; TVDSS is interpolated
 * from the log dataset when one exists and lab Sw is always left empty --
 * both gaps are reported by the validation report rather than guessed.
 */
export const seedSwHeightFromSamples = async (
  projectUid: string,
  dataset: SwHeightDatasetKind = 'core'
) => {
  const resource = `/sw-height/projects/${projectUid}/logs/from-samples?dataset=${dataset}`;
  try {
    const response = await API.post<SwHeightApiResponse<SwHeightSeedResult>>(
      resource
    );
    if (!response.data.content) {
      throw new Error(response.data.message || 'Seeding returned no content.');
    }
    return response.data.content;
  } catch (error) {
    throw new Error(
      apiErrorMessage(error, 'Failed to build the dataset from project samples.')
    );
  }
};

export const getSwHeightData = async (
  projectUid: string,
  dataset: SwHeightDatasetKind = 'log'
) => {
  const resource = `/sw-height/projects/${projectUid}?dataset=${dataset}`;
  /*
   * The backend answers "nothing imported yet" with a 400 whose body carries the
   * reason ("No well logs imported for project ..."), so the message has to be
   * pulled out of the response. Letting axios' own Error through instead gave the
   * page "Request failed with status code 400", which it could not recognise as
   * an empty project -- so a project awaiting its first import rendered as an
   * error rather than as the empty state.
   */
  try {
    const response = await API.get<SwHeightApiResponse<SwHeightData>>(resource);
    if (!response.data.content) {
      throw new Error(response.data.message || 'Sw-height fetch returned no content.');
    }
    return response.data.content;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to load Sw-height data.'));
  }
};

export const updateSwHeightSettings = async (
  projectUid: string,
  settings: SwHeightSettings,
  dataset: SwHeightDatasetKind = 'log'
) => {
  const resource = `/sw-height/projects/${projectUid}/settings?dataset=${dataset}`;
  try {
    const response = await API.put<SwHeightApiResponse<SwHeightSettings>>(
      resource,
      settings
    );
    return response.data.content;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to save Sw-height settings.'));
  }
};

export const computeSwHeight = async (
  projectUid: string,
  payload: SwHeightComputePayload,
  dataset: SwHeightDatasetKind = 'log'
) => {
  const resource = `/sw-height/projects/${projectUid}/compute?dataset=${dataset}`;
  try {
    const response = await API.post<
      SwHeightApiResponse<SwHeightComputeResponseContent>
    >(resource, payload);
    if (!response.data.content) {
      throw new Error(response.data.message || 'Sw-height compute returned no content.');
    }
    return response.data.content;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to compute Sw-height.'));
  }
};

export const downloadSwHeightTemplate = async (
  dataset: SwHeightDatasetKind = 'log'
) => {
  const resource = `/sw-height/template?dataset=${dataset}`;
  try {
    const response = await API.get<Blob>(resource, { responseType: 'blob' });
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to download well log template.'));
  }
};

export const getSwHeightDefaults = async (projectUid: string) => {
  const resource = `/sw-height/projects/${projectUid}/parameter-defaults`;
  const response = await API.get<SwHeightApiResponse<SwHeightDefaults>>(resource);
  return response.data.content;
};

export const clearSwHeightLogs = async (
  projectUid: string,
  dataset: SwHeightDatasetKind = 'log'
) => {
  const resource = `/sw-height/projects/${projectUid}/logs?dataset=${dataset}`;
  try {
    const response = await API.delete<SwHeightApiResponse<{ cleared: boolean }>>(
      resource
    );
    if (!response.data.content) {
      throw new Error(response.data.message || 'Clear returned no content.');
    }
    return response.data.content;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to clear well logs.'));
  }
};
